/* eslint-disable no-console */
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { appConfig } from 'constants/appConfig';
import { useAppSelector } from 'hooks/store';
import { UserDataSelector } from 'store/user';
import { sessionToken } from 'utils/session';

import { SignalREvents } from 'constants/signalR';
import { SignalRContext } from 'contexts/signalR';
import { enqueueSnackbar } from 'notistack';
import { isSoftphonePopupView } from 'components/CallScribe/popout';
import { isCallScribeWindowView } from 'components/CallScribe/callScribeWindow';

/** How long after the tenant is known the hub may take to connect before the user is warned. */
export const CONNECTION_WARNING_MS = 10000;

const notifyConnectionState = (message: string, variant: 'warning' | 'success'): void => {
  if (isSoftphonePopupView() || isCallScribeWindowView()) {
    return;
  }
  enqueueSnackbar(message, { variant });
};

/**
 * @interface IProps
 * @description Child component parameters that will be wrapped in this component
 */
interface IProps {
  children: ReactNode;
}

/**
 * @function SignalRProvider
 * @description A functional component that provides SignalR connection functionality
 * @param {ReactNode} children - The child components to be rendered within this component
 */
const SignalRProvider: React.FC<IProps> = ({ children }) => {
  const [methods, setMethods] = useState<string[]>([]);
  const [established, setEstablished] = useState<boolean>(false);

  const user = useAppSelector(UserDataSelector);

  // Signed in means a session token exists or the user's tenant has loaded. The token is there
  // from the first render of any page opened with a session, so the connection starts while the
  // user profile is still loading - as it did before it was gated - instead of only once the
  // tenant arrives, which left too little time before the "connection in progress" warning below.
  const signedIn = !!user?.tenantId || !!sessionToken();

  /**
   * @description Establish SignalR connection
   *
   * Built only for a signed-in user. The hub delivers to tenant groups alone, so a connection
   * opened before sign-in can never receive anything - and this provider wraps every route,
   * including the login page, whose every load used to open one: most of production's hub
   * connections never subscribed to a tenant at all. With no connection object the start effect
   * below does nothing; once signed in it starts this one, and stops it again on sign-out or when
   * the provider unmounts.
   */
  const signalRConnection = useMemo(
    () =>
      (!!appConfig.signalRUrl &&
        signedIn &&
        new HubConnectionBuilder()
          .withUrl(appConfig.signalRUrl)
          .withAutomaticReconnect()
          .build()) ||
      undefined,
    [signedIn],
  );

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  });

  useEffect(() => {
    if (!signalRConnection) return;
    signalRConnection.onreconnected(() => {
      if (userRef.current?.tenantId && signalRConnection.state === HubConnectionState.Connected) {
        signalRConnection.send(SignalREvents.TenantSubscription, userRef.current.tenantId);
      }
    });
  }, [signalRConnection]);

  /**
   * @description Use effect to start and stop SignalR connection
   */
  useEffect(() => {
    if (signalRConnection) {
      signalRConnection.start();
    }

    return () => {
      if (signalRConnection) {
        signalRConnection.stop();
      }
    };
  }, [signalRConnection]);

  /**
   * @description Use effect to handle tenant subscription
   */
  useEffect(() => {
    if (signalRConnection && user.tenantId && !established) {
      const interval = setInterval(() => {
        console.log(signalRConnection.state);
        // If the SignalR connection state is "Connected", send TenantSubscription
        if (signalRConnection.state === HubConnectionState.Connected) {
          clearInterval(interval);
          signalRConnection.send(SignalREvents.TenantSubscription, user?.tenantId);
        }
      }, 1000);

      // Warnings for a connection that is genuinely slow or failing. They were at 4 and 5 seconds,
      // which a sign-in in the same page - where the connection can only start once the tenant has
      // loaded - routinely exceeds on a healthy connection (5 s measured on DEV). Cleared when this
      // effect re-runs or unmounts, so a timer from a superseded connection never fires.
      const firstWarning = setTimeout(() => {
        if (signalRConnection.state !== HubConnectionState.Connected) {
          console.log('Error while connection to resource. Please reload this page');
          notifyConnectionState('Connection in progress.', 'warning');
        }

        if (!user?.tenantId) {
          console.log('tenant id was not provided properly', user?.tenantId);
          notifyConnectionState('Connection in progress.', 'warning');
        }
      }, CONNECTION_WARNING_MS);

      const secondWarning = setTimeout(() => {
        if (signalRConnection.state !== HubConnectionState.Connected) {
          notifyConnectionState('Connection in progress.', 'warning');
        }
      }, CONNECTION_WARNING_MS + 5000);

      const connectionCheck = setInterval(() => {
        if (signalRConnection.state === HubConnectionState.Connected && user.tenantId) {
          notifyConnectionState('Connection Established', 'success');
          setEstablished(true);
          clearInterval(connectionCheck);
        }
      }, 1000);

      return () => {
        clearTimeout(firstWarning);
        clearTimeout(secondWarning);
      };
    }

    return undefined;
  }, [signalRConnection, user, established]);

  const registerMethod = useCallback((arg: string) => {
    setMethods(prev => (prev.includes(arg) ? prev : [...prev, arg]));
  }, []);

  const contextValue = useMemo(
    () => ({
      connection: signalRConnection,
      established,
      methods,
      setMethods: registerMethod,
    }),
    [signalRConnection, established, methods, registerMethod],
  );

  return <SignalRContext.Provider value={contextValue}>{children}</SignalRContext.Provider>;
};

export default SignalRProvider;
